import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges, TemplateRef, ViewChild, AfterViewInit, OnChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModalConfig, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { BaseFormModalComponent } from 'components/modal/base-form-modal.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TimeInputComponent } from 'components/time-input/time-input.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { FILE_OBJECT_TYPE_COMMUNICATION_BACKOFFICE } from 'types/file.interface';
import { BackofficeReadingRoomSubmissionCard, RescheduleReadingRoomSubmissionRequest } from 'types/service.interface';
import { MAX_LENGTH_ADDITIONAL_INFO, getControlsWithErrors, timeFormatValidator, timeRangeValidator } from 'util/form.utility';
@Component({
  selector: 'app-request-another-time-specification',
  standalone: true,
  imports: [
    SpinnerComponent,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    TooltipComponent,
    DatePickerComponent,
    TimeInputComponent
  ],
  templateUrl: './request-another-time-specification.component.html',
  styleUrl: './request-another-time-specification.component.scss'
})
export class RequestAnotherTimeSpecificationComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_ADDITIONAL_INFO;
  @Input() isOpen!: boolean;
  @Input() allowFiles: boolean = true;
  @Input() modalTitle: string = '';
  @Input() application!: BackofficeReadingRoomSubmissionCard;
  @Output() update: EventEmitter<BackofficeReadingRoomSubmissionCard> = new EventEmitter<BackofficeReadingRoomSubmissionCard>();
  @ViewChild('content') content!: TemplateRef<any>;
  isSubmittingFiles = false;
  data!: BackofficeReadingRoomSubmissionCard;
  override fileUploadComponentCount = 1;
  readonly communicationFileType = FILE_OBJECT_TYPE_COMMUNICATION_BACKOFFICE;
  isCrudLoading = false;
  shouldRenderTooltip = false;
  public readonly dateRangeMode = DateRangeMode;
  reasonMaxLength = MAX_LENGTH_ADDITIONAL_INFO;
  get rescheduleDate(): AbstractControl<any, any> | null {
    return this.form.get('rescheduleDate');
  }
  get rescheduleTimeFrom(): AbstractControl<any, any> | null {
    return this.form.get('rescheduleTimeFrom');
  }
  get rescheduleTimeTo(): AbstractControl<any, any> | null {
    return this.form.get('rescheduleTimeTo');
  }
  get rescheduleReason(): AbstractControl<any, any> | null {
    return this.form.get('rescheduleReason');
  }

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);
      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      rescheduleDate: ['', [Validators.required]],
      rescheduleTimeFrom: ['', [Validators.required, timeFormatValidator()]],
      rescheduleTimeTo: [null, [timeFormatValidator(), timeRangeValidator('rescheduleTimeFrom')]],
      rescheduleReason: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]]
    });
  }

  protected override close(): void {
    super.close();
  }

  handleSave(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;
      const args: RescheduleReadingRoomSubmissionRequest = {
        ...this.form.value,
        readingRoomSubmissionId: this.application.id
      };

      this.apiService.rescheduleApplicationRequest(args).subscribe((response: BackofficeReadingRoomSubmissionCard) => {
        this.toastService.showSuccess('requestAnotherTimeSpecification', '', {
          shouldTranslate: true
        });
        this.close();
        this.update.emit(response);
        this.onSubmitSuccess();
      });
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.requestAdditionalNotes');
    }
  }

  onDateSelected(date: string, key: string): void {
    this.form.get(key)?.setValue(date);
    this.form.get(key)?.markAsTouched();
  }

  onTimeChange(time: string | null, key: string): void {
    this.form.get(key)?.setValue(time);
    this.form.get(key)?.markAsTouched();
  }

  markAsTouched(key: string): void {
    this.form.get(key)?.markAsTouched();
  }
}
